import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class OrderNotificationService {

  private ordersSubject = new BehaviorSubject<any[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  orders$ = this.ordersSubject.asObservable();
  unreadCount$ = this.unreadCountSubject.asObservable();

  private orderSub!: Subscription;
  private connSub!: Subscription; // 🔧 NEW
  private started = false;

  constructor(
    private http: HttpClient,
    private socketService: SocketService
  ) {}

  startListening() {
    if (this.started) return;

    this.started = true;

    // Load existing notifications
    this.fetchOrders();

    // Refresh order list whenever backend pushes
    // notification to ADMIN_NOTIFICATION room
    this.orderSub = this.socketService
      .onOrderNotification()
      .subscribe((data) => {

        console.log('📦 New order notification received:', data);

        this.fetchOrders();
      });

    // 🔧 NEW — resync on every (re)connect too. This is the safety
    // net: if a push event is ever missed (join-room race, brief
    // disconnect, dropped packet), we still self-correct the moment
    // the socket is connected — without needing a manual page refresh.
    this.connSub = this.socketService.connected$.subscribe(() => {
      console.log('🔄 Socket (re)connected — resyncing orders');
      this.fetchOrders();
    });
  }

  private fetchOrders() {

    this.http
      .get<any>(
        `${environment.API_ENDPOINT}module/admin/grocery/messages`
      )
      .subscribe({
        next: (res) => {

          console.log('Order list raw response:', res);

          const orders: any[] =
            Array.isArray(res?.data?.orders)
              ? res.data.orders
              : Array.isArray(res?.data?.messages)
              ? res.data.messages
              : Array.isArray(res?.data)
              ? res.data
              : [];

          console.log('Orders extracted:', orders);

          const normalized = orders.map((o) => {

            const supplementary = o?.supplementary || {};

            return {
              _id: o?._id,

              orderId:
                o?.orderId ||
                supplementary?.orderId ||
                o?._id,

              orderNumber:
                o?.orderNumber ??
                supplementary?.orderNo ??
                o?.orderId ??
                'N/A',

              customerName:
                o?.customer?.fname
                  ? `${o.customer.fname} ${o.customer.lname || ''}`.trim()
                  : o?.customerName ??
                    supplementary?.customerName ??
                    'Unknown',

              customerPhone:
                o?.customerPhone ??
                supplementary?.customerPhone ??
                '',

              amount:
                o?.totalAmount ??
                o?.amount ??
                supplementary?.totalAmount ??
                0,

              status:
                supplementary?.status ??
                o?.status ??
                'pending',

              title:
                o?.title ??
                'NEW_GROCERY_ORDER',

              body:
                o?.body ??
                '',

              createdAt:
                o?.createdAt ??
                supplementary?.createdAt,

              seen:
                o?.isRead ??
                o?.seen ??
                false,

              supplementary: supplementary
            };
          });

          console.log('✅ Normalized orders:', normalized);

          this.ordersSubject.next(normalized);

          this.unreadCountSubject.next(
            normalized.filter((o) => !o.seen).length
          );
        },

        error: (err) => {
          console.error('❌ Order fetch error:', err);
        }
      });
  }

  markOrderSeen(orderId: string) {

    const selectedOrder = this.ordersSubject.value.find(
      (o) => o._id === orderId
    );

    if (!selectedOrder) {
      console.warn(
        '⚠️ Notification not found:',
        orderId
      );

      return;
    }

    const payload = {
      messageId: selectedOrder._id
    };

    console.log(
      '📖 Mark notification as read:',
      payload
    );

    const updated = this.ordersSubject.value.map((o) =>
      o._id === selectedOrder._id
        ? {
            ...o,
            seen: true
          }
        : o
    );

    this.ordersSubject.next(updated);

    this.unreadCountSubject.next(
      updated.filter((o) => !o.seen).length
    );

    this.http
      .put<any>(
        `${environment.API_ENDPOINT}module/admin/grocery/messages`,
        payload
      )
      .subscribe({

        next: (res) => {

          console.log(
            '✅ Notification marked as read:',
            res
          );

        },

        error: (err) => {

          console.error(
            '❌ Failed to mark notification as read:',
            err
          );

          this.fetchOrders();

        }

      });
  }

  stopListening() {

    this.orderSub?.unsubscribe();
    this.connSub?.unsubscribe(); // 🔧 NEW

    this.started = false;
  }
}